import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ReceiptText, Landmark } from 'lucide-react'
import type { PageMeta } from '../../../lib/permissions/pageMeta'
import { PageHeader } from '../../../components/ui/PageHeader'
import { EmptyState } from '../../../components/ui/EmptyState'
import { Spinner } from '../../../components/ui/Spinner'
import { SearchInput } from '../../../components/ui/SearchInput'
import { Pagination } from '../../../components/ui/Pagination'
import { DateRangePicker } from '../../../components/ui/DateRangePicker'
import { ExportButton } from '../../../components/common/ExportButton'
import { HeaderFilter } from '../../../components/ui/HeaderFilter'
import { HeaderRangeFilter } from '../../../components/ui/HeaderRangeFilter'
import { SummaryStrip } from '../../../components/ui/SummaryStrip'
import { SnCell, SnHeader } from '../../../components/ui/SnCell'
import { useDateRange } from '../../../hooks/useDateRange'
import { useDebounce } from '../../../hooks/useDebounce'
import { useCurrency } from '../../../hooks/useCurrency'
import { useAuthStore } from '../../../store/authStore'
import { shortDate } from '../../../lib/paymentReport/format'
import { cn } from '../../../utils/cn'
import {
  fetchAllExpensePayments,
  toExpenseExportRow,
  type ExpensePaymentFilters,
  type ExpensePaymentRow,
} from '../../../lib/paymentReport/expensePayments'
import {
  type Option,
  EXPENSE_TYPE_OPTIONS,
  DEFAULT_STATUS_OPTIONS,
} from '../../../lib/paymentReport/filterOptions'

const PILL_TABS = ['today', 'week', 'month', 'year', 'custom'] as const

export const pageMeta: PageMeta = {
  key: 'trading.payments.expenses',
  label: 'Expense Payments',
  module: 'Trading',
  section: 'Payments',
  description: 'Expenses paid from bank or cash, including accrual settlements',
  defaultPermissions: {
    owner: 'view', admin: 'view', manager: 'view',
    accountant: 'none', cashier: 'none', viewer: 'none',
  },
}

// Distinct {label,value} options from the loaded rows (Expense sources options
// in-memory — the full set is already fetched).
function uniqueOptions(rows: ExpensePaymentRow[], pick: (r: ExpensePaymentRow) => string | null): Option[] {
  const set = new Set<string>()
  for (const r of rows) {
    const v = pick(r)
    if (v) set.add(v)
  }
  return [...set].sort().map(v => ({ value: v, label: v }))
}

export default function ExpensePaymentsTab() {
  const { tenant } = useAuthStore()
  const tid = tenant?.id ?? ''
  const { format } = useCurrency()
  const dr = useDateRange('month')

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [showVoided, setShowVoided] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Header filters (Feature 4) — applied in-memory on allRows.
  const [fCategory, setFCategory] = useState<string[]>([])
  const [fType, setFType] = useState<string[]>([])
  const [fMethod, setFMethod] = useState<string[]>([])
  const [fStatus, setFStatus] = useState<string[]>([])
  const [amtMin, setAmtMin] = useState<number | null>(null)
  const [amtMax, setAmtMax] = useState<number | null>(null)

  const filtersActive =
    fCategory.length > 0 || fType.length > 0 || fMethod.length > 0 || fStatus.length > 0 || amtMin != null || amtMax != null
  function clearFilters() {
    setFCategory([]); setFType([]); setFMethod([]); setFStatus([]); setAmtMin(null); setAmtMax(null)
  }

  const filters = useMemo<ExpensePaymentFilters>(
    () => ({ from: dr.from, to: dr.to, search: debouncedSearch.trim(), showVoided }),
    [dr.from, dr.to, debouncedSearch, showVoided],
  )

  useEffect(() => { setPage(1) }, [filters])
  // Header-filter changes also reset to page 1.
  useEffect(() => { setPage(1) }, [fCategory, fType, fMethod, fStatus, amtMin, amtMax])

  // A two-source union is fetched whole; the page slice, total and export all
  // derive from this single list (see reader for why it can't be server-paged).
  const { data: allRows = [], isLoading } = useQuery({
    queryKey: ['expense-payments-all', tid, filters],
    enabled: !!tid,
    queryFn: () => fetchAllExpensePayments(tid, filters),
    staleTime: 30 * 1000,
  })

  // Options derived from the loaded set. Type/Status use fixed domains.
  const categoryOptions = useMemo(() => uniqueOptions(allRows, r => r.category), [allRows])
  const methodOptions = useMemo(() => uniqueOptions(allRows, r => r.method), [allRows])

  // Header filters applied in-memory (AND across all active filters).
  const filteredRows = useMemo(() => {
    const cat = new Set(fCategory), meth = new Set(fMethod), typ = new Set(fType), stat = new Set(fStatus)
    return allRows.filter(r => {
      if (cat.size && !cat.has(r.category)) return false
      if (typ.size && !typ.has(r.typeLabel)) return false
      if (meth.size && !(r.method != null && meth.has(r.method))) return false
      if (stat.size && !stat.has(r.isVoided ? 'voided' : 'posted')) return false
      if (amtMin != null && r.amount < amtMin) return false
      if (amtMax != null && r.amount > amtMax) return false
      return true
    })
  }, [allRows, fCategory, fType, fMethod, fStatus, amtMin, amtMax])

  const totalCount = filteredRows.length
  const periodTotal = useMemo(
    () => filteredRows.filter(r => !r.isVoided).reduce((s, r) => s + r.amount, 0),
    [filteredRows],
  )
  const maxPage = Math.max(1, Math.ceil(totalCount / pageSize))
  useEffect(() => { if (page > maxPage) setPage(maxPage) }, [page, maxPage])
  const pageRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title="Expense Payments"
        subtitle="Expenses paid from bank or cash, including accrual settlements"
        actions={
          <ExportButton
            filename={`expense-payments-${filters.from}-to-${filters.to}`}
            data={filteredRows.map(r => ({ id: r.id }))}
            fetchData={async () => filteredRows.map(toExpenseExportRow)}
            columns={[
              { key: 'date', header: 'Date', isDate: true },
              { key: 'category', header: 'Category' },
              { key: 'payee', header: 'Vendor/Payee' },
              { key: 'type', header: 'Type' },
              { key: 'method', header: 'Method' },
              { key: 'reference', header: 'Reference' },
              { key: 'amount', header: 'Amount', isCurrency: true },
              { key: 'journal', header: 'Journal' },
              { key: 'status', header: 'Status' },
            ]}
          />
        }
      />

      <SummaryStrip
        count={totalCount}
        amount={format(periodTotal)}
        filtersActive={filtersActive}
        onClearFilters={clearFilters}
      />

      {/* Controls: date pills · search · show-voided */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <DateRangePicker {...dr} tabs={[...PILL_TABS]} />
        <div className="flex items-center gap-3">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            placeholder="Description, vendor or ref…"
            containerClassName="w-full sm:w-64"
          />
          <label className="flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-400"
              checked={showVoided}
              onChange={e => setShowVoided(e.target.checked)}
            />
            Show voided
          </label>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : pageRows.length === 0 ? (
        <EmptyState
          icon={<ReceiptText className="h-8 w-8" />}
          title="No expense payments"
          description="No paid expenses or accrual settlements in this period. Try widening the date range or clearing filters."
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <SnHeader />
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <HeaderFilter label="Category" options={categoryOptions} selected={fCategory} onChange={setFCategory} />
                      <span className="text-gray-300">·</span>
                      <HeaderFilter label="Type" options={EXPENSE_TYPE_OPTIONS} selected={fType} onChange={setFType} />
                    </span>
                  </th>
                  <th className="px-4 py-3">Vendor/Payee</th>
                  <th className="px-4 py-3">
                    <HeaderFilter label="Method" options={methodOptions} selected={fMethod} onChange={setFMethod} />
                  </th>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3 text-right">
                    <HeaderRangeFilter
                      label="Amount"
                      min={amtMin}
                      max={amtMax}
                      onChange={({ min, max }) => { setAmtMin(min); setAmtMax(max) }}
                    />
                  </th>
                  <th className="px-4 py-3">Journal</th>
                  <th className="px-4 py-3">
                    <HeaderFilter label="Status" options={DEFAULT_STATUS_OPTIONS} selected={fStatus} onChange={setFStatus} align="right" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, i) => (
                  <tr key={`${r.kind}-${r.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                    <SnCell page={page} pageSize={pageSize} index={i} />
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{shortDate(r.date)}</td>
                    <td className="px-4 py-3 text-gray-900">
                      <span className="font-medium">{r.category}</span>
                      {r.typeLabel !== 'One-time' && (
                        <span className="ml-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                          {r.typeLabel === 'Prepayment' ? 'Prepayment' : 'Settlement'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.payee}</td>
                    <td className="px-4 py-3 capitalize text-gray-600">
                      {r.method
                        ? (
                          <span className="inline-flex items-center gap-1">
                            {r.methodIsAccount && (
                              <Landmark className="h-3 w-3 text-gray-400" aria-label="Paid from account (method not recorded)" />
                            )}
                            <span className={cn(r.methodIsAccount && 'italic text-gray-500')}>{r.method}</span>
                          </span>
                        )
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.reference ?? '—'}</td>
                    <td className={cn('px-4 py-3 text-right font-semibold whitespace-nowrap', r.isVoided ? 'text-gray-400 line-through' : 'text-gray-900')}>
                      {format(r.amount)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500" title={r.journalId ?? undefined}>
                      {r.journalNumber ?? (r.journalId ? r.journalId.slice(0, 8) : '—')}
                    </td>
                    <td className="px-4 py-3">
                      {r.isVoided
                        ? <span className="badge badge-red">Voided</span>
                        : <span className="badge badge-green">Posted</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-400">
            <span>{totalCount} payment{totalCount !== 1 ? 's' : ''}</span>
            <span>Total (excl. voided): <strong className="text-gray-700">{format(periodTotal)}</strong></span>
          </div>

          <div className="px-4 pb-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              onPageChange={setPage}
              onPageSizeChange={s => { setPageSize(s); setPage(1) }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
